import { z } from 'zod';
/**
 * Saved replies and ticket merging for the support desk (docs/adr/0102).
 *
 * A saved (canned) reply is plain text, like the ticket reply composer, with `{{placeholders}}` filled in when it is
 * inserted into a reply. Shared replies belong to the workspace (owners and admins manage them); personal ones belong to
 * one person and nobody else sees them. Guests and client contacts never see either.
 */
/** The placeholders a saved reply may use, filled from the ticket, the person inserting it and the workspace. */
export const CANNED_PLACEHOLDERS = [
    'contact.first_name',
    'contact.name',
    'ticket.number',
    'ticket.key',
    'ticket.subject',
    'agent.first_name',
    'workspace.name',
    'portal.link',
];
/** What people read for each placeholder (Settings and the editor's insert menu). */
export const CANNED_PLACEHOLDER_LABELS = {
    'contact.first_name': "Client contact's first name",
    'contact.name': "Client contact's full name",
    'ticket.number': 'Ticket number',
    'ticket.key': 'Ticket key',
    'ticket.subject': 'Ticket subject',
    'agent.first_name': 'Your first name',
    'workspace.name': 'Workspace name',
    'portal.link': 'Client portal link',
};
const PLACEHOLDER = /\{\{\s*([a-z_.]{1,40})\s*\}\}/g;
/**
 * Fills a saved reply's placeholders. A placeholder that is unknown, or known but has no value for this ticket (a contact
 * with no name, the portal switched off), is left exactly as written, so the person sees it before sending. Pure, so the
 * API, web and phone fill text the same way.
 */
export function fillCannedReply(body, values) {
    const unfilled = [];
    const text = body.replace(PLACEHOLDER, (whole, key) => {
        const value = CANNED_PLACEHOLDERS.includes(key) ? values[key] : undefined;
        if (value === undefined || value === null || value === '') {
            if (!unfilled.includes(whole))
                unfilled.push(whole);
            return whole;
        }
        return value;
    });
    return { text, unfilled };
}
/** Placeholders a body uses that Chapsi doesn't know (shown as a warning in the editor, never refused). */
export function unknownPlaceholders(body) {
    const out = [];
    for (const m of body.matchAll(PLACEHOLDER)) {
        if (!CANNED_PLACEHOLDERS.includes(m[1]) && !out.includes(m[0]))
            out.push(m[0]);
    }
    return out;
}
/** A shortcut typed after "#" in the composer: lower case letters, digits, dashes and underscores. */
export const CannedShortcut = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9][a-z0-9_-]{0,31}$/, 'Use up to 32 letters, numbers, dashes or underscores');
export const CannedReply = z.object({
    id: z.uuid(),
    name: z.string(),
    shortcut: z.string().nullable(),
    body: z.string(),
    /** A status to set when the reply is sent with it ("Send and set Waiting on client"); null for none. */
    statusId: z.uuid().nullable(),
    /** True for the workspace's shared replies; false for the caller's own. */
    shared: z.boolean(),
    /** The caller may edit and delete it: their own, or a shared one for owners and admins. */
    editable: z.boolean(),
    useCount: z.number().int(),
    lastUsedAt: z.string().nullable(),
    updatedAt: z.string(),
});
export const CannedReplySort = z.enum(['usage', 'recent', 'name']);
export const ListCannedRepliesQuery = z.object({
    /** Matches the name, shortcut or body, ignoring case. */
    q: z.string().trim().max(100).optional(),
    scope: z.enum(['all', 'shared', 'mine']).default('all'),
    sort: CannedReplySort.default('usage'),
});
export const CannedReplyList = z.object({
    items: z.array(CannedReply),
    /** Owners and admins may add shared replies. */
    canManageShared: z.boolean(),
});
export const CreateCannedReplyInput = z.object({
    name: z.string().trim().min(1).max(120),
    shortcut: CannedShortcut.nullable().optional(),
    body: z.string().trim().min(1).max(20000),
    statusId: z.uuid().nullable().optional(),
    /** Shared with the team (owners and admins only) or the caller's own. */
    shared: z.boolean().default(false),
});
export const UpdateCannedReplyInput = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    shortcut: CannedShortcut.nullable().optional(),
    body: z.string().trim().min(1).max(20000).optional(),
    statusId: z.uuid().nullable().optional(),
});
/** Inserting a reply into a ticket: the text with placeholders filled, counted as one use. */
export const UseCannedReplyInput = z.object({ workItemId: z.uuid() });
export const UsedCannedReply = z.object({
    text: z.string(),
    /** Placeholders left in the text because they are unknown or have no value here. */
    unfilled: z.array(z.string()),
    /** The reply's default status, when it still applies to this ticket; null otherwise. */
    statusId: z.uuid().nullable(),
});
// --- Ticket merging ---------------------------------------------------------------------------------------------------
export const MergeCandidatesQuery = z.object({ q: z.string().trim().max(100).optional() });
export const MergeCandidate = z.object({
    id: z.uuid(),
    key: z.string(),
    title: z.string(),
    statusId: z.uuid(),
    clientId: z.uuid().nullable(),
    clientName: z.string(),
    /** The same client as the ticket being merged: suggested first. */
    sameClient: z.boolean(),
    requesterEmail: z.string().nullable(),
    updatedAt: z.string(),
});
export const MergeCandidateList = z.object({ items: z.array(MergeCandidate) });
export const MergeTicketInput = z.object({
    /** The ticket to merge into. */
    targetId: z.uuid(),
    /** Email the merged ticket's requester that the conversation continues on the other ticket. Off by default. */
    notifyRequester: z.boolean().default(false),
    /** Required when the two tickets belong to different clients (or one has none). */
    acrossClients: z.boolean().default(false),
});
export const MergedTicket = z.object({
    sourceId: z.uuid(),
    targetId: z.uuid(),
    targetKey: z.string(),
    /** The record of this merge, which undoing it names (docs/adr/0103). */
    mergeId: z.uuid(),
    moved: z.object({
        emails: z.number().int(),
        notes: z.number().int(),
        attachments: z.number().int(),
        timeEntries: z.number().int(),
        people: z.number().int(),
    }),
    /** Billed time stays on the merged ticket: an approved invoice locks it (docs/adr/0054). */
    billedTimeKept: z.number().int(),
});
/**
 * Undo a merge (docs/adr/0103). The path names the merged ticket. `mergeId` names the merge being undone (the success
 * toast and the drawer send it): when the ticket has been merged again since, the undo is refused (409) instead of
 * undoing a different merge. Without it, the ticket's current merge is undone.
 */
export const UnmergeTicketInput = z.object({ mergeId: z.uuid().optional() });
export const UnmergedTicket = z.object({
    sourceId: z.uuid(),
    sourceKey: z.string(),
    targetId: z.uuid(),
    targetKey: z.string(),
    /** Rows the merge moved that went back to the merged ticket. */
    movedBack: z.object({
        emails: z.number().int(),
        notes: z.number().int(),
        attachments: z.number().int(),
        subtasks: z.number().int(),
        timeEntries: z.number().int(),
        timers: z.number().int(),
    }),
    /** Watchers (client contacts) and staff followers the merge had added to the target, now taken off it. */
    removed: z.object({ watchers: z.number().int(), followers: z.number().int() }),
    /** Tickets merged into the merged ticket earlier, pointing at it again. */
    restoredMerges: z.number().int(),
    /**
     * Rows that stayed on the target: time entries invoiced since the merge (an approved invoice locks them,
     * docs/adr/0054), and rows deleted or moved somewhere else since.
     */
    stayed: z.object({ invoicedTime: z.number().int(), gone: z.number().int() }),
});
//# sourceMappingURL=canned-replies.js.map